/**
 * What a visible Voice of the graph can play (ADR-0068, ADR-0077): the audition clip of its
 * voiceprint, reached through the audited clip route by the recording and the voice number the
 * node carries, or the reason there is nothing to add. The card's own status line already says
 * when a voice has too little speech or is not prepared yet; this adds the HUMINT gate. Pure.
 */
import type { AudioEvidence } from '@/components/investigations/entity-detail/audio-evidence'
import { ApiError } from '@/services/api/client'

export type VoiceClipState =
  /** The clip route of the voice; what plays is typed as the WAV it is. */
  | { kind: 'ready'; url: string }
  /** The clip sits behind the HUMINT extension, which is off here. */
  | { kind: 'humint_off' }
  /** Nothing to add: no voice to derive a clip from, or the status line already explains. */
  | { kind: 'silent' }

/** The audited clip route of one numbered voice of a recording; a viewer of the case may read it. */
export function voiceClipUrl(caseId: string, documentId: string, voice: number): string {
  return `/api/v1/cases/${caseId}/documents/${encodeURIComponent(documentId)}/voices/${voice}/audio`
}

export interface VoiceClipGate {
  /** The HUMINT extension is on for this deployment. */
  enabled: boolean
  /** The extension list is still being read. */
  isLoading: boolean
}

/** The clip state of a voice from what its node says and whether HUMINT is on. */
export function voiceClipState(
  caseId: string,
  evidence: Pick<AudioEvidence, 'kind' | 'voice' | 'documentId' | 'matchingStatus'>,
  gate: VoiceClipGate
): VoiceClipState {
  const prepared =
    evidence.matchingStatus !== 'insufficient_speech' && evidence.matchingStatus !== 'pending'
  if (evidence.kind !== 'voice' || evidence.voice === null || !evidence.documentId || !prepared)
    return { kind: 'silent' }
  if (gate.isLoading) return { kind: 'silent' }
  if (!gate.enabled) return { kind: 'humint_off' }
  return { kind: 'ready', url: voiceClipUrl(caseId, evidence.documentId, evidence.voice) }
}

/** Why a clip could not be fetched, by what the server answered. */
export type AudioFailure = 'forbidden' | 'missing' | 'unavailable' | 'other'

export function audioFailureOf(error: unknown): AudioFailure {
  if (!(error instanceof ApiError)) return 'other'
  if (error.status === 403) return 'forbidden'
  if (error.status === 404) return 'missing'
  return error.status === 503 ? 'unavailable' : 'other'
}
